import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import MemberLayout from '../components/MemberLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Bot, User, BookOpen, Sparkles, Send } from 'lucide-react';

const SUGGEST = ['Apa itu Rising Wedge?', 'Jelaskan Double Top', 'Beri saya latihan soal divergence', 'Bagaimana cara menghitung lot?'];

export default function AITutor() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim()) return;
    setMessages(m => [...m, { role: 'user', content: msg }]);
    setInput(''); setLoading(true);
    try {
      const { data } = await api.post('/ai/chat', { message: msg, session_id: session });
      setSession(data.session_id);
      setMessages(m => [...m, { role: 'assistant', content: data.answer }]);
    } catch (e) { toast.error(e.response?.data?.detail || 'AI error'); setMessages(m => m.slice(0, -1)); }
    finally { setLoading(false); }
  };

  return (
    <MemberLayout>
      <h1 className="font-display text-2xl sm:text-3xl font-bold">AI Gold Tutor</h1>
      <p className="text-muted-foreground mt-1">Grounded pada materi kursus. Bukan nasihat keuangan.</p>

      <Card className="mt-6 flex flex-col h-[65vh]">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-10">
              <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--brand-ocean)/0.12)] flex items-center justify-center mx-auto"><Bot className="h-7 w-7 text-sky-600" /></div>
              <p className="text-muted-foreground mt-3 text-sm">Tanya apa saja tentang materi Gold Trading.</p>
              <div className="flex flex-wrap gap-2 justify-center mt-4">{SUGGEST.map(s => <button key={s} onClick={() => send(s)} className="rounded-full border px-3 py-1.5 text-xs hover:bg-muted">{s}</button>)}</div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`}>
              {m.role === 'assistant' && <div className="h-8 w-8 rounded-lg bg-[hsl(var(--brand-ocean)/0.12)] flex items-center justify-center flex-shrink-0"><Bot className="h-4 w-4 text-sky-600" /></div>}
              {m.role === 'user' ? (
                <div className="bg-[hsl(var(--brand-ink))] text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm max-w-[80%]">{m.content}</div>
              ) : (
                <div className="space-y-2 max-w-[85%]">
                  {m.content.source_material && (
                    <div className="rounded-xl bg-muted/60 border p-3">
                      <Badge variant="outline" className="mb-2"><BookOpen className="h-3 w-3 mr-1" />Sumber Materi</Badge>
                      <p className="text-sm text-foreground/85 whitespace-pre-wrap">{m.content.source_material}</p>
                    </div>
                  )}
                  <div className="rounded-xl bg-[hsl(var(--brand-ocean)/0.08)] border border-[hsl(var(--brand-ocean)/0.2)] p-3">
                    <Badge variant="secondary" className="mb-2"><Sparkles className="h-3 w-3 mr-1" />Penjelasan AI</Badge>
                    <p className="text-sm text-foreground/90 whitespace-pre-wrap">{m.content.ai_explanation}</p>
                    {m.content.related_lesson && <p className="text-xs text-muted-foreground mt-2">Lesson terkait: {m.content.related_lesson}</p>}
                  </div>
                  <p className="text-[11px] text-muted-foreground">{m.content.disclaimer}</p>
                </div>
              )}
              {m.role === 'user' && <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0"><User className="h-4 w-4" /></div>}
            </div>
          ))}
          {loading && <div className="flex gap-3"><div className="h-8 w-8 rounded-lg bg-[hsl(var(--brand-ocean)/0.12)] flex items-center justify-center"><Bot className="h-4 w-4 text-sky-600" /></div><div className="flex gap-1 items-center px-4"><span className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce" /><span className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{animationDelay:'0.15s'}} /><span className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{animationDelay:'0.3s'}} /></div></div>}
          <div ref={endRef} />
        </div>
        <div className="border-t p-3 flex gap-2">
          <Textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Tanya tentang materi..." className="min-h-[44px] max-h-32 resize-none" data-testid="ai-input" />
          <Button onClick={() => send()} disabled={loading} className="h-11 bg-[hsl(var(--brand-ink))] text-white" data-testid="ai-send-button"><Send className="h-4 w-4" /></Button>
        </div>
      </Card>
    </MemberLayout>
  );
}
